import React, { useMemo } from 'react';
import Button from '../../components/ui/Button';
import EmptyState from '../../components/ui/EmptyState';
import {
  EmployeeDashboardGrid,
  EmployeeSummaryCard,
  EmployeeWorkspaceEmptyState,
  EmployeeWorkspaceSection
} from '../../components/workforce/EmployeeWorkspace';
import {
  CERTIFICATION_STATUS,
  certificationName,
  classifyCertification
} from './certificationStatus';

function formatDate(value) {
  if (!value) return 'Not recorded';
  const dateOnly = String(value).slice(0, 10);
  return /^\d{4}-\d{2}-\d{2}$/.test(dateOnly) ? dateOnly : 'Not recorded';
}

function CertificationList({ title, description, rows, emptyMessage }) {
  return (
    <EmployeeWorkspaceSection title={title} description={description}>
      {rows.length ? (
        <ul className="employee-training__records" data-testid={`training-certifications-${title.toLowerCase().replace(/[^a-z]+/g, '-')}`}>
          {rows.map(document => (
            <li key={document.id}>
              <div>
                <strong>{certificationName(document)}</strong>
                <span>{document.certificationStatus}</span>
              </div>
              <dl>
                <div><dt>Recorded</dt><dd><time dateTime={String(document.uploadedAt || '')}>{formatDate(document.uploadedAt)}</time></dd></div>
                <div><dt>Expiration</dt><dd>{document.expiresAt ? <time dateTime={String(document.expiresAt)}>{formatDate(document.expiresAt)}</time> : 'No expiration recorded'}</dd></div>
              </dl>
            </li>
          ))}
        </ul>
      ) : <EmptyState compact icon="·" headline={emptyMessage} body="No certification records match this status for this employee." />}
    </EmployeeWorkspaceSection>
  );
}

export default function EmployeeTrainingCertificationsWorkspace({
  profileId,
  documents = [],
  canReadDocuments = false,
  documentsEnabled = true,
  onManageCertifications = null,
  now = Date.now()
}) {
  const rows = useMemo(() => (
    canReadDocuments && documentsEnabled
      ? documents
        .filter(document => document.documentType === 'certification'
          && (!profileId || Number(document.employeeProfileId) === Number(profileId)))
        .map(document => ({ ...document, certificationStatus: classifyCertification(document, now) }))
      : []
  ), [canReadDocuments, documents, documentsEnabled, now, profileId]);

  const byStatus = status => rows.filter(document => document.certificationStatus === status);
  const active = byStatus(CERTIFICATION_STATUS.ACTIVE);
  const expiring = byStatus(CERTIFICATION_STATUS.EXPIRING);
  const expired = byStatus(CERTIFICATION_STATUS.EXPIRED);
  const archived = byStatus(CERTIFICATION_STATUS.ARCHIVED);
  const certificationAvailable = documentsEnabled && canReadDocuments;

  return (
    <div className="employee-training" data-testid="employee-training-certifications-workspace">
      <EmployeeWorkspaceSection
        title="Qualification summary"
        description="Employee certification records supported by the Documents module"
        actions={certificationAvailable && onManageCertifications ? <Button size="sm" variant="secondary" onClick={onManageCertifications}>Manage certifications</Button> : null}
      >
        {!documentsEnabled ? (
          <EmptyState compact icon="·" headline="Certifications are unavailable" body="The Documents module is disabled for this tenant. Training capabilities remain separate." />
        ) : !canReadDocuments ? (
          <EmptyState compact icon="·" headline="Certification access restricted" body="Certification records require employee document permission." />
        ) : (
          <EmployeeDashboardGrid columns="wide">
            <EmployeeSummaryCard label="Active" value={active.length} />
            <EmployeeSummaryCard label="Expiring" value={expiring.length} tone={expiring.length ? 'warning' : 'neutral'} />
            <EmployeeSummaryCard label="Expired" value={expired.length} tone={expired.length ? 'warning' : 'neutral'} />
            <EmployeeSummaryCard label="Archived" value={archived.length} />
          </EmployeeDashboardGrid>
        )}
      </EmployeeWorkspaceSection>

      {certificationAvailable ? (
        <>
          <CertificationList title="Active certifications" description="Current records with no expiration or an expiration beyond 30 days" rows={active} emptyMessage="No active certifications" />
          <CertificationList title="Expiring and expired certifications" description="Records classified only from their stored expiration dates" rows={[...expired, ...expiring]} emptyMessage="No expiring or expired certifications" />
          <CertificationList title="Archived certifications" description="Preserved records archived through the authoritative certification workflow" rows={archived} emptyMessage="No archived certifications" />
        </>
      ) : null}

      <EmployeeDashboardGrid>
        <EmployeeWorkspaceSection title="Certification requirements" description="Current model availability">
          <EmployeeWorkspaceEmptyState title="Certification requirements are not configured" description="Certification requirements are not configured in the current data model." />
        </EmployeeWorkspaceSection>
        <EmployeeWorkspaceSection title="Training and learning history" description="Planned capability">
          <EmployeeWorkspaceEmptyState title="Training history is planned" description="The current data model does not contain course assignments, completions, or employee learning history." />
        </EmployeeWorkspaceSection>
        <EmployeeWorkspaceSection title="Mandatory training" description="Planned capability">
          <EmployeeWorkspaceEmptyState title="Mandatory training is planned" description="The current data model does not contain mandatory learning requirements, assignments, or due dates." />
        </EmployeeWorkspaceSection>
        <EmployeeWorkspaceSection title="Learning and development context" description="Planned capability">
          <EmployeeWorkspaceEmptyState title="Learning and development context is planned" description="The current data model does not contain learning assignments or development-plan actions." />
        </EmployeeWorkspaceSection>
      </EmployeeDashboardGrid>

      <EmployeeWorkspaceSection title="Future AI assistance" description="Reserved capability — no AI processing is active">
        <EmployeeWorkspaceEmptyState title="AI learning assistance is planned" description="Future governed capabilities may include learning recommendations, renewal guidance, and role qualification gap review." />
      </EmployeeWorkspaceSection>
    </div>
  );
}
